import React from 'react'
import PropTypes from 'prop-types'
import JoiBrowser from 'joi-browser'
import NaturalCompare from 'natural-compare'
import _ from 'underscore'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Box,
  Button,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react'
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import DateRangePicker from 'components/DateRangePicker'
import CustomerAccountSelector from 'components/CustomerAccountSelector'
import { MemoizedSelect } from 'components/MemoizedInput'
import TableSpinner from 'components/TableSpinner'
import CustomerStatsReadQuery from './__query__/index.customerStats.read.query'
import MccReadQuery from 'components/MccMncSelector/__query__/index.mcc.read.query'

//Status labels and their colors
const statusItems = [
  { name: 'Delivered', color: '#F59E0B' },
  { name: 'Accepted', color: '#10B981' },
  { name: 'Failed', color: '#0EA5E9' },
  { name: 'Rejected', color: '#EF4444' },
  { name: 'Undeliverable', color: '#4F46E5' },
  { name: 'None', color: '#8B5CF6' },
  { name: 'Expired', color: '#64748B' }
]


const colors = statusItems.map(item => item.color)
const pieChartStyle = { outline: 'none' }
const svgSurfaceStyle = {
  '& svg.recharts-surface': { outline: 'none !important' },
  '& .recharts-sector': { outline: 'none !important' },
  '& .recharts-layer': { outline: 'none !important' },
}

const _RenderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent <= 0 || percent < 0.05) return null

  const _radius = innerRadius + ((outerRadius - innerRadius) * 0.62)
  const _x = cx + (_radius * Math.cos((-midAngle * Math.PI) / 180))
  const _y = cy + (_radius * Math.sin((-midAngle * Math.PI) / 180))

  return (
    <text
      x={_x}
      y={_y}
      fill='#1A202C'
      stroke='white'
      strokeWidth='4'
      paintOrder='stroke'
      textAnchor='middle'
      dominantBaseline='central'
      fontSize='12px'
      fontWeight='700'>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}


const CustomerStatsGraph = ({ title }) => {
  const _isCurrentViewMobile = useBreakpointValue({ 'base': true, 'sm': true, 'md': false, 'lg': false, 'xl': false })
  const _isCurrentViewMedium = useBreakpointValue({ 'base': false, 'sm': false, 'md': true, 'lg': false, 'xl': false })

  const [customerAccountId, setCustomerAccountId] = React.useState('')
  const [mcc, setMcc] = React.useState('')
  const [startDate, setStartDate] = React.useState(Object.DateRangeProvider()?.startDate)
  const [endDate, setEndDate] = React.useState(Object.DateRangeProvider()?.endDate)
  const [chartData, setChartData] = React.useState([])
  const [error, setError] = React.useState('')

  const _HandleCustomerAccountChange = React.useCallback(({ target }) => {
    setCustomerAccountId(target.value)
    setError('')
  }, [])

  const _HandleMccChange = React.useCallback(({ target }) => {
    setMcc(target.value)
    setError('')
  }, [])

  const _QueryMccRead = useQuery(MccReadQuery, { 'variables': { 'take': 1000, 'skip': 0 }, 'fetchPolicy': Object.React.App.fetchPolicy, 'pollInterval': Object.React.App.pollInterval })

  const [QueryCustomerStats, QueryCustomerStatsResponse] = useLazyQuery(CustomerStatsReadQuery, {
    'fetchPolicy': Object.React.App.fetchPolicy
  })

  React.useEffect(() => {
    if (!QueryCustomerStatsResponse?.data?.CustomerAccountStatsReport?._reportData) return

    const _reportData = QueryCustomerStatsResponse.data.CustomerAccountStatsReport._reportData ?? []
    const _data = []
    _reportData.forEach(item => {
      const _fields = [
        { 'name': 'Delivered', 'value': Number(item.deliveredsmscount) },
        { 'name': 'Accepted', 'value': Number(item.acceptedsmscount) },
        { 'name': 'Failed', 'value': Number(item.failedsmscount) },
        { 'name': 'Rejected', 'value': Number(item.rejectedsmscount) },
        { 'name': 'Undeliverable', 'value': Number(item.undeliverablesmscount) },
        { 'name': 'None', 'value': Number(item.nonesmscount) },
        { 'name': 'Expired', 'value': Number(item.expiredsmscount) }
      ]
      _fields.forEach(field => {
        if (field.value > 0) _data.push(field)
      })
    })
    setChartData(_data)
  }, [QueryCustomerStatsResponse?.data])

  const _HandleDateRangeChange = React.useCallback((rangeValue) => {
    setStartDate(rangeValue?.selection?.startDate)
    setEndDate(rangeValue?.selection?.endDate)
  }, [])


  const _HandleSubmit = React.useCallback(() => {
    const _JoiSchema = JoiBrowser.object({
      'customerAccountId': JoiBrowser.string().required(),
      'country': JoiBrowser.string().required()
    }).options({ 'allowUnknown': true })

    const _JoiSchemaValidate = _JoiSchema.validate({
      customerAccountId,
      country: mcc
    })

    if (_JoiSchemaValidate.error) {
      setError(_JoiSchemaValidate.error?.message)
      return
    }

    const _id = customerAccountId?.includes('(') ? customerAccountId.split('(')[1].replace(')', '').trim() : customerAccountId
    const _countryName = mcc?.split('(')?.[0]?.trim() || mcc

    QueryCustomerStats({
      'variables': {
        'customerAccountId': _id,
        country: _countryName,
        startDate,
        endDate
      }
    }).catch(e => console.error('CustomerAccountStatsReport failed:', e))
  }, [customerAccountId, mcc, startDate, endDate, QueryCustomerStats])

  const _total = React.useMemo(() =>
    chartData.reduce((sum, item) => sum + (item.value || 0), 0),
    [chartData]
  )

  const _TooltipFormatter = React.useCallback((value, name) => {
    const _percent = _total > 0 ? ((Number(value) / _total) * 100).toFixed(2) : '0.00'
    return [`${value} (${_percent}%)`, name]
  }, [_total])

  const _mccOptions = React.useMemo(() =>
    _.compact(_QueryMccRead?.data?.MccRead?.map?.(j => 'READ_SUCCESSFUL' === j.status ? `${j.countryName} (${j.mcc})` : void 0))?.sort((a, b) => NaturalCompare(a, b)),
    [_QueryMccRead?.data?.MccRead]
  )

  const _cellElements = React.useMemo(() =>
    chartData.map((entry, index) => {
      const statusItem = statusItems.find(item => item.name === entry.name)
      return (
        <Cell
          key={`${entry.name}-${index}`}
          fill={statusItem?.color || colors[index % colors.length]}
          style={{ outline: 'none' }}
        />
      )
    }),
    [chartData]
  )

  return (
    <Box
      bg='white'
      border='1px solid #E2E8F0'
      borderRadius='20px'
      p={_isCurrentViewMobile ? '14px' : '22px'}
      w='100%'
      display='flex'
      flexDirection='column'
      boxShadow='14px 17px 40px 4px rgba(112, 144, 176, 0.10)'
    >
      <Flex justify='space-between' alignItems='center' width='100%' gap='12px' flexDir={{ 'base': 'column', 'md': 'row' }}>
        <Text
          color='rgba(43,54,116,1)'
          fontWeight='700'
          fontSize={{ 'base': 'sm', 'md': 'md' }}
          textAlign='left'>
          {title}
        </Text>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          w={_isCurrentViewMobile ? '100%' : { 'md': '100%', 'lg': 'auto' }}
          setSelectDate={_HandleDateRangeChange}
        />
      </Flex>

      <Flex mt='16px' gap='12px' flexWrap='wrap' alignItems='flex-start'>
        <CustomerAccountSelector
          w={_isCurrentViewMobile ? '100%' : { 'md': '180px', 'lg': '240px' }}
          name='customerAccountId'
          placeholder='Select Customer Account'
          customerType='WHOLESALE'
          value={customerAccountId}
          onChange={_HandleCustomerAccountChange}
          isRequired={true}
          isInvalid={error?.includes('customerAccountId')}
        />

        <MemoizedSelect
          w={_isCurrentViewMobile ? '100%' : { 'md': '160px', 'lg': '220px' }}
          disabled={_QueryMccRead?.loading}
          name='mcc'
          value={mcc}
          height='100%'
          placeholder='Select Country'
          options={_mccOptions}
          isInvalid={error?.includes('country')}
          isRequired={true}
          onChange={_HandleMccChange}
        />

        <Button
          bg='purple.50'
          color='purple'
          onClick={_HandleSubmit}
          isLoading={QueryCustomerStatsResponse?.loading}
          w={_isCurrentViewMobile ? '100%' : 'auto'}
          fontWeight='500'
          ml='auto'>
          Submit
        </Button>
      </Flex>

      {error && (
        <Text color='red.500' fontSize='sm' mt='8px'>
          {error}
        </Text>
      )}

      <Box
        mt='12px'
        h={_isCurrentViewMobile ? '400px' : { 'md': '380px', 'lg': '350px' }}
        borderRadius='16px'
        p='20px'
        w='100%'
        minW={0}
        minH={0}
        position='relative'
        overflow='hidden'
        sx={svgSurfaceStyle}>
        {QueryCustomerStatsResponse?.loading
          ? <TableSpinner isLoading={true} isEmpty={false} />
          : chartData.length === 0
            ? <TableSpinner isLoading={false} isEmpty={true} />
            : (
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart
                  style={pieChartStyle}
                  tabIndex={-1}>
                  <Pie
                    data={chartData}
                    dataKey='value'
                    nameKey='name'
                    cx='50%'
                    cy='50%'
                    outerRadius={_isCurrentViewMobile ? '80%' : (_isCurrentViewMedium ? '90%' : '100%')}
                    innerRadius={0}
                    labelLine={false}
                    stroke='none'
                    strokeWidth={0}
                    label={_RenderPieLabel}
                    isAnimationActive={false}
                    style={{ outline: 'none' }}>
                    {_cellElements}
                  </Pie>
                  <Tooltip formatter={_TooltipFormatter} />
                </PieChart>
              </ResponsiveContainer>
            )}
      </Box>

      <Flex mt='16px' gap='16px' flexWrap='wrap' alignItems='center' justify='center'>
        {statusItems.map((status, idx) => (
          <Flex key={idx} alignItems='center' gap='8px'>
            <Box
              width='12px'
              height='12px'
              borderRadius='2px'
              bg={status.color}
            />
            <Text fontSize='sm' color='gray.600'>
              {status.name}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box >
  )
}

export default React.memo(CustomerStatsGraph)
